import { Developer } from '../types/developer';

// Airtable field mappings
export const AIRTABLE_FIELDS = {
  NAME: 'Name',
  EMAIL: 'Email',
  PHOTO: 'Photo',
  LOCATION: 'Location',
  SKILLS: 'Skills',
  EXPERIENCE_LEVEL: 'Experience Level',
  HOURLY_RATE: 'Hourly Rate',
  AVAILABILITY: 'Availability',
  ENGLISH_LEVEL: 'English Level',
  BIO: 'Bio',
  PORTFOLIO_URL: 'Portfolio URL',
  LINKEDIN_URL: 'LinkedIn URL',
  GITHUB_URL: 'GitHub URL',
  CV_FILE: 'CV File',
  STATUS: 'Status',
  DATE_ADDED: 'Date Added',
  YEARS_EXPERIENCE: 'Years Experience',
  TIME_ZONE: 'Time Zone'
} as const;

/**
 * Validate developer data from Airtable
 */
export const validateDeveloperData = (data: any): boolean => {
  const requiredFields = [
    AIRTABLE_FIELDS.NAME,
    AIRTABLE_FIELDS.EMAIL,
    AIRTABLE_FIELDS.SKILLS,
    AIRTABLE_FIELDS.EXPERIENCE_LEVEL,
    AIRTABLE_FIELDS.HOURLY_RATE,
    AIRTABLE_FIELDS.AVAILABILITY,
    AIRTABLE_FIELDS.ENGLISH_LEVEL,
    AIRTABLE_FIELDS.BIO,
    AIRTABLE_FIELDS.STATUS
  ];

  return requiredFields.every(field => {
    const value = data.get(field);
    return value !== undefined && value !== null && value !== '';
  });
};

/**
 * Get USD to SEK exchange rate from environment
 */
const getExchangeRate = (): number => {
  const rate = process.env.REACT_APP_EXCHANGE_RATE_USD_SEK;
  return rate ? parseFloat(rate) : 10.50; // Default fallback rate
};

/**
 * Format hourly rate for display in SEK
 */
export const formatHourlyRate = (rateUSD: number): string => {
  const exchangeRate = getExchangeRate();
  const rateSEK = Math.round(rateUSD * exchangeRate);
  const roundedSEK = Math.round(rateSEK / 10) * 10;
  return `${roundedSEK} SEK`;
};

/**
 * Convert USD amount to SEK
 */
export const convertUSDToSEK = (amountUSD: number): number => {
  const exchangeRate = getExchangeRate();
  return Math.round(amountUSD * exchangeRate);
};

/**
 * Format any USD amount to SEK for display
 */
export const formatAmountInSEK = (amountUSD: number): string => {
  const amountSEK = convertUSDToSEK(amountUSD);
  return `${amountSEK} SEK`;
};

/**
 * Get availability status color
 */
export const getAvailabilityColor = (availability: string): string => {
  switch (availability) {
    case 'Available':
      return 'text-green-600 bg-green-100';
    case 'Busy':
      return 'text-yellow-600 bg-yellow-100';
    case 'Not Available':
      return 'text-red-600 bg-red-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

/**
 * Get experience level badge color
 */
export const getExperienceLevelColor = (level: string): string => {
  switch (level) {
    case 'Junior':
      return 'bg-gray-100 text-gray-800';
    case 'Mid-level':
      return 'bg-green-100 text-green-800';
    case 'Senior':
      return 'bg-purple-100 text-purple-800';
    case 'Lead':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format skills array for display
 */
export const formatSkills = (skills: string[], maxDisplay: number = 3): string => {
  if (skills.length <= maxDisplay) {
    return skills.join(', ');
  }
  return skills.slice(0, maxDisplay).join(', ') + ` +${skills.length - maxDisplay} more`;
};

/**
 * Calculate total cost for selected developers in SEK
 */
export const calculateTotalCost = (developers: Developer[], hoursPerMonth: number = 160): number => {
  const totalUSD = developers.reduce((total, developer) => {
    return total + (developer.hourlyRate * hoursPerMonth);
  }, 0);
  return convertUSDToSEK(totalUSD);
};

/**
 * Get developer's full name initials
 */
export const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}; 